import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { StatusChip, vehicleStatusTone } from "@/components/StatusChip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus } from "lucide-react";

export default function Vehicles() {
  const [searchParams] = useSearchParams();
  const [vehicles, setVehicles] = useState([]);
  const [filter, setFilter] = useState(searchParams.get("status") || "all");
  const [open, setOpen] = useState(false);
  const [chassis, setChassis] = useState("");
  const [reg, setReg] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    const params = filter !== "all" ? { status: filter } : {};
    api.get("/vehicles", { params }).then((r) => setVehicles(r.data)).catch((e) => toast.error(errMsg(e)));
  };
  useEffect(() => { load(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/vehicles", { chassis_number: chassis, registration_number: reg });
      toast.success("Vehicle added");
      setOpen(false); setChassis(""); setReg("");
      load();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const setStatus = async (v, status) => {
    try {
      await api.patch(`/vehicles/${v.vehicle_id}/status`, { status });
      toast.success(`${v.vehicle_id} → ${status}`);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div className="space-y-5" data-testid="vehicles-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Vehicles</h1>
        <div className="flex gap-3">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-48 bg-card border-border" data-testid="vehicle-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="Assigned">Assigned</SelectItem>
              <SelectItem value="Unassigned">Unassigned</SelectItem>
              <SelectItem value="Under Maintenance">Under Maintenance</SelectItem>
            </SelectContent>
          </Select>
          <Button data-testid="add-vehicle-btn" onClick={() => setOpen(true)}
            className="bg-emerald-500 hover:bg-emerald-600 text-white font-semibold">
            <Plus className="h-4 w-4 mr-1.5" /> Add Vehicle
          </Button>
        </div>
      </div>

      <div className="card-tactical overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-border">
            <th className="p-3">Vehicle ID</th><th className="p-3">Chassis Number</th><th className="p-3">Last 4</th>
            <th className="p-3">Reg. Number</th><th className="p-3">Status</th><th className="p-3">Rider</th><th className="p-3">Set Status</th>
          </tr></thead>
          <tbody>
            {vehicles.map((v) => (
              <tr key={v.vehicle_id} className="border-b border-border/50 hover:bg-slate-50" data-testid={`vehicle-row-${v.vehicle_id}`}>
                <td className="p-3 font-mono font-medium">{v.vehicle_id}</td>
                <td className="p-3 font-mono text-xs">{v.chassis_number}</td>
                <td className="p-3 font-mono">{v.chassis_last4}</td>
                <td className="p-3 font-mono text-xs">{v.registration_number || "—"}</td>
                <td className="p-3"><StatusChip label={v.status} tone={vehicleStatusTone(v.status)} testid={`vehicle-status-${v.vehicle_id}`} /></td>
                <td className="p-3 font-mono text-xs">{v.rider_id || "—"}</td>
                <td className="p-3">
                  <Select value={v.status} onValueChange={(s) => setStatus(v, s)}>
                    <SelectTrigger className="h-8 w-44 bg-slate-50 border-border text-xs" data-testid={`vehicle-status-select-${v.vehicle_id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Assigned">Assigned</SelectItem>
                      <SelectItem value="Unassigned">Unassigned</SelectItem>
                      <SelectItem value="Under Maintenance">Under Maintenance</SelectItem>
                    </SelectContent>
                  </Select>
                </td>
              </tr>
            ))}
            {vehicles.length === 0 && (
              <tr><td colSpan={7} className="p-8 text-center text-slate-500" data-testid="vehicles-empty">No vehicles yet. Add your first vehicle.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border" data-testid="add-vehicle-modal">
          <DialogHeader><DialogTitle className="font-heading">Add Vehicle</DialogTitle></DialogHeader>
          <form onSubmit={add} className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-xs font-medium uppercase tracking-wider text-slate-400">Full Chassis Number *</label>
              <Input data-testid="vehicle-chassis-input" required value={chassis} onChange={(e) => setChassis(e.target.value)}
                className="bg-slate-50 border-border font-mono" placeholder="e.g. MB1HFL3S9PT012345" />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium uppercase tracking-wider text-slate-400">Registration Number</label>
              <Input data-testid="vehicle-reg-input" value={reg} onChange={(e) => setReg(e.target.value)}
                className="bg-slate-50 border-border font-mono" placeholder="e.g. KA01AB1234" />
            </div>
            <Button type="submit" disabled={busy} data-testid="add-vehicle-submit"
              className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-semibold">
              {busy ? "Adding…" : "Add Vehicle"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
